import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Grid,
    IconButton,
    InputAdornment,
    MuiThemeProvider,
    Paper,
    TextField,
    Tooltip,
    Typography,
    withStyles
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import MUIDataTable from 'mui-datatables';
import Auxiliary from 'hoc/Auxiliary';
import Spinner from 'components/UI/Spinner/Spinner';
import useDebounce from 'components/TrustCenter/UI/Debounce/Debounce';
import {
    getAllV2ProviderConfig,
    getAllV2ProviderSummary
} from 'components/TrustCenter/store/actions/agreementV2Action';
import { PRIMARY_BUTTON_LIGHT_THEME } from 'components/UI/Button/ButtonMuiTheme';
import { SYSTEM_ADMIN } from 'constants/Constants';
import VisibilityConnectionStyles from './VisibilityConnectionStyles';
import { MUIDATATABLE_THEME, SEARCH_BAR_DARK_THEME } from './VisibilityConnectionsGridMuiTheme';

/** @typedef {{ providerAccountId: string, providerName: string, agreementCount: number }} ConnectionsProviderRow */

const extractProviderId = (p) =>
    p?.providerAccountId ?? p?.providerAccount ?? p?.providerId ?? null;

const extractProviderName = (p) => p?.providerName ?? p?.providerAccountName ?? p?.name ?? '-';

/** Prefer API-provided totals; otherwise count summary rows per provider. */
const buildAgreementCountByProvider = (summaryList) => {
    /** @type {Map<string, { explicit?: number, rows: number }>} */
    const acc = new Map();
    summaryList.forEach((p) => {
        const id = extractProviderId(p);
        if (!id) {
            return;
        }
        const entry = acc.get(id) ?? { rows: 0 };
        const n =
            p.agreementCount ??
            p.sharingAgreementCount ??
            p.count ??
            p.totalAgreements ??
            p.numberOfAgreements;
        if (typeof n === 'number' && !Number.isNaN(n)) {
            entry.explicit = Math.max(entry.explicit ?? 0, n);
        } else {
            entry.rows += 1;
        }
        acc.set(id, entry);
    });
    /** @type {Map<string, number>} */
    const out = new Map();
    acc.forEach((entry, id) => {
        out.set(id, entry.explicit !== undefined ? entry.explicit : entry.rows);
    });
    return out;
};

/**
 * Lists all visibility providers from provider-config with agreement counts from provider-summary.
 * Providers with agreements are sorted first (higher counts before lower), then by name.
 * Navigates to /connections/v2/provider/:providerAccountId for the agreement grid.
 */
const ConnectionsProvidersList = (props) => {
    const { classes, history, accountUuid, selectedAccount, sessionValue } = props;
    const [allProviders, setAllProviders] = useState(/** @type {ConnectionsProviderRow[]} */([]));
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearch = useDebounce(searchValue, 400);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(false);

        const providerSummaryAccountId =
            sessionValue?.userRole === SYSTEM_ADMIN ? accountUuid : undefined;

        Promise.all([
            getAllV2ProviderConfig(),
            getAllV2ProviderSummary(providerSummaryAccountId).catch(() => ({ data: [] }))
        ])
            .then(([configRes, summaryRes]) => {
                if (cancelled) {
                    return;
                }
                const rawConfig = configRes.data;
                const configList = Array.isArray(rawConfig) ? rawConfig : rawConfig?.items ?? [];

                const rawSummary = summaryRes.data;
                const summaryList = Array.isArray(rawSummary)
                    ? rawSummary
                    : rawSummary?.items ?? [];

                const countByProvider = buildAgreementCountByProvider(summaryList);

                const byId = new Map();
                configList.forEach((p) => {
                    const id = extractProviderId(p);
                    if (!id) {
                        return;
                    }
                    byId.set(id, {
                        providerAccountId: id,
                        providerName: extractProviderName(p),
                        agreementCount: countByProvider.get(id) ?? 0
                    });
                });

                summaryList.forEach((p) => {
                    const id = extractProviderId(p);
                    if (!id || byId.has(id)) {
                        return;
                    }
                    byId.set(id, {
                        providerAccountId: id,
                        providerName: extractProviderName(p),
                        agreementCount: countByProvider.get(id) ?? 0
                    });
                });

                const merged = [...byId.values()].sort((a, b) => {
                    const aHas = a.agreementCount > 0;
                    const bHas = b.agreementCount > 0;
                    if (aHas !== bHas) {
                        return aHas ? -1 : 1;
                    }
                    if (a.agreementCount !== b.agreementCount) {
                        return b.agreementCount - a.agreementCount;
                    }
                    return (a.providerName || '').localeCompare(b.providerName || '', undefined, {
                        sensitivity: 'base'
                    });
                });

                setAllProviders(merged);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError(true);
                    setAllProviders([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [accountUuid, sessionValue?.userRole]);

    const filteredProviders = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        if (!q) {
            return allProviders;
        }
        return allProviders.filter(
            (p) =>
                (p.providerName || '').toLowerCase().includes(q) ||
                (p.providerAccountId || '').toLowerCase().includes(q)
        );
    }, [allProviders, debouncedSearch]);

    const tableData = useMemo(
        () => filteredProviders.map((p) => [p.providerName, p.agreementCount, p.providerAccountId]),
        [filteredProviders]
    );

    const accountSearch =
        sessionValue?.userRole === SYSTEM_ADMIN && selectedAccount?.id
            ? `?accountId=${encodeURIComponent(selectedAccount.id)}`
            : '';

    const goToProvider = (providerAccountId, providerName) => {
        history.push({
            pathname: `/connections/v2/provider/${providerAccountId}`,
            ...(accountSearch ? { search: accountSearch } : {}),
            state: { providerName }
        });
    };

    const clearSearch = () => setSearchValue('');

    const columns = [
        {
            name: 'providerName',
            label: 'Provider',
            options: {
                customBodyRender: (value, tableMeta) => {
                    const id = tableData[tableMeta?.dataIndex]?.[2];
                    const count = tableData[tableMeta?.dataIndex]?.[1] ?? 0;
                    return (
                        <Tooltip
                            title={
                                count > 0
                                    ? 'View sharing agreements for this provider'
                                    : 'View this provider (no sharing agreements yet)'
                            }
                        >
                            <Typography
                                variant="subtitle1"
                                className={classes.providerName}
                                id={'provider_' + id}
                            >
                                {value}
                            </Typography>
                        </Tooltip>
                    );
                }
            }
        },
        {
            name: 'agreementCount',
            label: '# of Sharing Agreements',
            options: {
                customBodyRender: (value, tableMeta) => (
                    <Typography id={'agreement_count_' + tableMeta?.dataIndex}>{value}</Typography>
                )
            }
        },
        {
            name: 'providerAccountId',
            options: {
                display: false
            }
        }
    ];

    const options = {
        textLabels: {
            body: {
                noMatch: loadError
                    ? 'Unable to load providers'
                    : debouncedSearch.trim()
                        ? 'No providers match your search'
                        : 'No providers found'
            }
        },
        filter: false,
        download: false,
        responsive: 'vertical',
        selectableRows: 'none',
        serverSide: false,
        sort: true,
        search: false,
        print: false,
        viewColumns: false,
        customToolbar: () => (
            <MuiThemeProvider theme={PRIMARY_BUTTON_LIGHT_THEME}>
                <MuiThemeProvider theme={SEARCH_BAR_DARK_THEME}>
                    <TextField
                        value={searchValue}
                        variant="standard"
                        className={classes.searchBarDark}
                        id="connections-providers-search"
                        placeholder="Search by provider name or id"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                                <InputAdornment position="end">
                                    <Tooltip title="Clear search">
                                        <IconButton
                                            size="small"
                                            id="connections-providers-search-clear"
                                            onClick={clearSearch}
                                        >
                                            <CloseIcon
                                                fontSize="small"
                                                className={classes.closeIconDark}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ) : null
                        }}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </MuiThemeProvider>
            </MuiThemeProvider>
        ),
        onCellClick: (_cellData, cellMeta) => {
            if (cellMeta.colIndex !== 0 && cellMeta.colIndex !== 1) {
                return null;
            }
            const row = tableData[cellMeta.dataIndex];
            if (!row) {
                return null;
            }
            const [providerName, , providerAccountId] = row;
            goToProvider(providerAccountId, providerName);
            return null;
        }
    };

    return (
        <Auxiliary>
            <Paper className={sessionValue?.userRole === SYSTEM_ADMIN ? classes.vcPaperSysAdmin : classes.vcPaper}>
                <MuiThemeProvider theme={MUIDATATABLE_THEME}>
                    {loading ? (
                        <Spinner />
                    ) : (
                        <MUIDataTable
                            id="connections_providers_grid"
                            title={
                                <Grid container>
                                    <Grid item>
                                        <Typography id="pageTitle" className={classes.titleBar}>
                                            PROVIDERS
                                        </Typography>
                                    </Grid>
                                </Grid>
                            }
                            data={tableData}
                            columns={columns}
                            options={options}
                        />
                    )}
                </MuiThemeProvider>
            </Paper>
        </Auxiliary>
    );
};

const mapStateToProps = (state) => {
    const accountUuid = state.session.sessionValue.accountUuid;
    const selectedId = get(state, 'accounts.selectedAccount.id', accountUuid);
    return {
        accountUuid: selectedId,
        selectedAccount: state.accounts.selectedAccount,
        sessionValue: state.session.sessionValue
    };
};

export default withRouter(
    connect(mapStateToProps)(withStyles(VisibilityConnectionStyles)(ConnectionsProvidersList))
);
